/**
 * Created by Ning Jiang on 2021-06-29.
 */

import {ContinueScreen} from "../../core/screen/ContinueScreen";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DeadwoodRipLayers, Layers} from "../view/DeadwoodRipLayers";
import {DeadwoodRip} from "../DeadwoodRip";
import {Translation} from "../../core/utils/Translation";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {gsap, TimelineLite} from "gsap";
import {DeadwoodRipSound} from "../sound/DeadwoodRipSound";
import {BigWinCountUp} from "../winpresentation/presentations/bigwin/BigWinCountUp";
import {MathHelper} from "../../core/utils/MathHelper";
import {IWinRatioConfig} from "../../core/config/WinRatio";
import {DeadwoodRipGameConfig} from "../DeadwoodRipGameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

export class FreespinOutro extends ContinueScreen {

    private _image:PIXI.Sprite;
    private _spine:PIXI.spine.Spine;
    private _countUp:BigWinCountUp;

    constructor() {
        super(DeadwoodRipLayers.getLayer(Layers.SCREENS)!);

        this._image = new PIXI.Sprite();
        this._image.anchor.set(0.5);
        this._image.position.set(360, 360);
        this._container.addChild(this._image);

        this._played = new PIXI.Text("", new PIXI.TextStyle({
            fill : 0xffffff,
            fontFamily : OpenSans.FAMILY,
            wordWrap : false,
            fontWeight : FontWeight.BOLD,
            fontSize : 30
        }));
        this._played.anchor.set(0.5, 0.5);
        this._played.visible = false;
        this._container.addChild(this._played);

        this._countUp = new BigWinCountUp();
        this._container.addChild(this._countUp);
    }

    public show(onContinueCallBack:() => void):TimelineLite {
        const belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowStake();
        SlotGame.keypadPlugin.setWin(DeadwoodRip.model.parsedGameData.totalWin, true, belowStakeWinRestriction);
        SlotGame.keypadPlugin.hide();

        const spinPlayed = DeadwoodRip.model.parsedGameData.numberOfFreespinsPlayed;
        this._played.text = Translation.translation("Spins played: ${spinPlayed}").replace("${spinPlayed}", spinPlayed.toString());
        this._spine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("introOutro"));
        this._spine.autoUpdate = true;
        this._image.addChild(this._spine);
        const aniName = DeadwoodRip.model.data.mode === GameMode.FREESPIN_SUPER ? "outroSFS" : "outroFs";
        this._spine.state.setAnimation(0, aniName, true);
        // this._image.texture = DeadwoodRip.assetLoader.getTextures("fsintro/FS_outro")[0];
        return super.show(onContinueCallBack);
    }

    protected getShowAnimations():TimelineLite {
        const noWin = DeadwoodRip.model.parsedGameData.totalWin === 0 || BelowStakeWinRestriction.isWinBelowStake();
        const soundName = noWin ? DeadwoodRipSound.FREE_SPIN_END_BAD : DeadwoodRipSound.FREE_SPIN_END_GOOD;
        const tl = new TimelineLite();
        tl.add([
            gsap.to(this._image, {alpha : 1, duration : 0.6}),
            this._countUp.getCountUpAnimation(0, DeadwoodRip.model.parsedGameData.totalWin, noWin ? 0.01 : 2),
            () => {
                DeadwoodRip.initialCountUp.remove();
                DeadwoodRip.bigWinCountUp.remove();
                DeadwoodRip.waysWon.remove();
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.FREE_SPIN_END});
                DeadwoodRipSound.switchMusic(
                    {name : soundName, volume : 0},
                    {name : soundName, from : 0, to : 1, duration : 1},
                    {name : "", from : 100, to : 0, duration : 400}
                );
                if(!noWin) {
                    DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.COUNT_UP_COUNTER});
                }
            }
        ]);
        tl.add(() => {
            this._played.visible = true;
            if(!noWin) {
                DeadwoodRipSound.soundPlayer.stopTrack(DeadwoodRipSound.COUNT_UP_COUNTER);
                DeadwoodRipSound.soundPlayer.playTrack({name : this.getCountUpEndSoundName()});
            }
        });

        return tl;
    }

    private getCountUpEndSoundName():string {
        const totalWinRatio = MathHelper.roundToDecimals(DeadwoodRip.model.parsedGameData.totalWin / DeadwoodRip.model.parsedGameData.playedBetValue, 2);
        let ratioConfig:IWinRatioConfig;
        for(let i:number = DeadwoodRipGameConfig.WIN_RATIOS.length - 1; i >= 0; i--) {
            const config = DeadwoodRipGameConfig.WIN_RATIOS[i];
            if(totalWinRatio >= config.ratio) {
                ratioConfig = config;
                break;
            }
        }

        switch(ratioConfig!.winLevel) {
            case 4:
                return DeadwoodRipSound.AS_WIN_BIG;
            case 3:
                return DeadwoodRipSound.COUNT_UP_END_LARGE;
            case 2:
                return DeadwoodRipSound.COUNT_UP_END_MEDIUM;
            case 1:
                return DeadwoodRipSound.COUNT_UP_END_SMALL;
            default:
                return DeadwoodRipSound.COUNT_UP_END_SMALL;
        }
    }

    protected close():TimelineLite {
        const tl = super.close();
        tl.add(() => {
            DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.FREE_SPIN_OUTRO_END});
            DeadwoodRip.xBetController.featureVisible = true;
            DeadwoodRip.background.updateMode(DeadwoodRip.model.data.nextMode);
            DeadwoodRipSound.switchMusic({name : DeadwoodRipSound.MAIN_GAME_MUSIC, volume : 1});
        }, 0);
        tl.add(() => {
            this._spine.state.setEmptyAnimations(0);
            this._image.removeChild(this._spine);
            this._spine.destroy();
            this._played.visible = false;
        });

        return tl;
    }
}